import React from 'react';
import { Text, StyleSheet, Image, View, Link } from '@react-pdf/renderer';
import PM from '../../../../assets/images/bcc/Union.png';
import NO2 from '../../../../assets/images/bcc/Group_13.png';
import O3 from '../../../../assets/images/bcc/Group_14.png';

const styles = StyleSheet.create({
  title: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 14,
    color: '#4E73C3',
  },
  paragraph: {
    marginBottom: 10,
    fontSize: 11,
    color: '#000',
    lineHeight: 1.5,
    letterSpacing: -0.32,
  },
});

const SecondPage = () => {
  return (
    <View>
      <Text style={{ ...styles.title, marginBottom: 16 }}>
        What is air quality?
      </Text>
      <Text style={{ ...styles.paragraph, marginBottom: 32 }}>
        Air quality is the term we use to describe how polluted the air we
        breathe is. When air quality is poor, pollutants in the air may be
        hazardous to people, particularly those with lung or heart conditions.
      </Text>
      <Text style={{ ...styles.title, marginBottom: 16 }}>
        What are the sources of air pollution?
      </Text>
      <Text style={{ ...styles.paragraph, marginBottom: 16 }}>
        Air pollution is a complex mix of particles and gases of both natural
        and human origin. Particulate matter (PM) and nitrogen dioxide (NO
        <Text style={{ fontSize: 8 }}>2</Text>) are both major components of
        urban air pollution. Currently, there is no clear evidence of a safe
        level of exposure below which there is no risk of adverse health
        effects.
      </Text>
      <View
        style={{
          width: '100%',
          maxWidth: 499,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <View
          style={{
            width: 96,
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 12,
          }}
        >
          <Image style={{ width: 96, height: 64 }} src={PM} />
        </View>
        <Text
          style={{ flex: 1, fontSize: 11, marginBottom: 0, lineHeight: 1.5 }}
        >
          Particulate Matter consists of tiny particles that can be inhaled deep
          in the lungs. PM<Text style={{ fontSize: 8 }}>2.5</Text> refers to
          particles that are 2.5 micrometres or smaller. PM
          <Text style={{ fontSize: 8 }}>10</Text> refers to particles that are
          10 micrometres or smaller. Sources include fossil fuel residential
          heating systems, gasoline-powered vehicles, construction dust, etc.
        </Text>
      </View>
      <View
        style={{
          width: '100%',
          maxWidth: 499,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <View
          style={{
            width: 96,
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 12,
          }}
        >
          <Image style={{ width: 64, height: 64 }} src={NO2} />
        </View>
        <Text
          style={{ flex: 1, fontSize: 11, marginBottom: 0, lineHeight: 1.5 }}
        >
          Nitrogen Dioxide (NO<Text style={{ fontSize: 8 }}>2</Text>) is a
          reddish- brown gas. It is one of a group of highly reactive gases
          known as nitrogen oxides. Sources of nitrogen dioxide include vehicle
          exhaust, burning fossil fuels and industrial processes.
        </Text>
      </View>
      <View
        style={{
          width: '100%',
          maxWidth: 499,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 32,
        }}
      >
        <View
          style={{
            width: 96,
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 12,
          }}
        >
          <Image style={{ width: 64, height: 64 }} src={O3} />
        </View>
        <Text
          style={{ flex: 1, fontSize: 11, marginBottom: 0, lineHeight: 1.5 }}
        >
          Ozone (O<Text style={{ fontSize: 8 }}>3</Text>) is a gas. It is formed
          from the chemical reaction between air pollutants from vehicle
          exhausts etc.
        </Text>
      </View>
      <Text style={{ ...styles.title, marginBottom: 16 }}>
        What are the health effects of poor air quality?
      </Text>
      <Text style={{ ...styles.paragraph, marginBottom: 16 }}>
        Air pollution can have serious effects on health, especially for
        children as their lungs are still developing.  Health effects can be
        short or long term. The main health effects include:
      </Text>
      <View style={{ display: 'flex', flexDirection: 'row', marginBottom: 12 }}>
        <View style={{ flex: 1, marginRight: 12 }}>
          <Text
            style={{
              marginBottom: 4,
              fontSize: 11,
              fontFamily: 'Helvetica-Bold',
              color: '#4E73C3',
            }}
          >
            Respiratory Issues
          </Text>
          <Text style={styles.paragraph}>
            Exposure to pollutants can lead to asthma, bronchitis, and other
            respiratory problems, making it harder for children to breathe,
            especially during physical activities.
          </Text>
        </View>
        <View style={{ flex: 1 }}>
          <Text
            style={{
              marginBottom: 4,
              fontSize: 11,
              fontFamily: 'Helvetica-Bold',
              color: '#4E73C3',
            }}
          >
            Reduced Lung Function
          </Text>
          <Text style={styles.paragraph}>
            Long-term exposure can lead to a reduction in lung growth and
            function, affecting children's health as they grow.
          </Text>
        </View>
      </View>
      <View style={{ display: 'flex', flexDirection: 'row', marginBottom: 16 }}>
        <View style={{ flex: 1, marginRight: 12 }}>
          <Text
            style={{
              marginBottom: 4,
              fontSize: 11,
              fontFamily: 'Helvetica-Bold',
              color: '#4E73C3',
            }}
          >
            Worsening of Pre-Existing Conditions
          </Text>
          <Text style={styles.paragraph}>
            Children with asthma or allergies may find their symptoms worsening
            when air quality is poor.
          </Text>
        </View>
        <View style={{ flex: 1 }}>
          <Text
            style={{
              marginBottom: 4,
              fontSize: 11,
              fontFamily: 'Helvetica-Bold',
              color: '#4E73C3',
            }}
          >
            Cognitive Effects
          </Text>
          <Text style={styles.paragraph}>
            Studies suggest that poor air quality may even affect children’s
            concentration and performance in school.
          </Text>
        </View>
      </View>
      <Link
        style={{ fontSize: 10, fontWeight: 300, color: '#4A5568' }}
        src="https://www.gov.uk/government/publications/health-matters-air-pollution/health-matters-air-pollution"
      >
        https://www.gov.uk/government/publications/health-matters-air-pollution/health-matters-air-pollution
      </Link>
    </View>
  );
};

export default SecondPage;
